import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import { useIsMobile } from '@stores/AppCommonData';
import H4 from '@base/H4/H4';
import { useSelectorWithShallowEqual } from '@hooks/reduxHooks';
import { getPreviousSessions, getCurrentQuizType } from '@selectors/quizTaking';
import { mediaQuery, Spacings } from '@styles/index';
import { fullDateWithTime } from '@utils/dates';
import useTotalQuestions from '@cont/Quiz/Common/useTotalQuestions';
import pushRoute from '@utils/pushRoute';
import InfoCard from './InfoCard';
import useQuizStatus from '../Common/useQuizStatus';

const Wrapper = styled.div`
  margin-top: ${Spacings.SPACING_14B};
  grid-column: span 24;
  display: flex;
  justify-content: space-between;

  ${mediaQuery.uptoTablet} {
    grid-column: auto;
    width: 100%;
    margin-top: ${Spacings.SPACING_4B};
    flex-flow: column;
  }
`;

const Heading = styled(H4)`
  padding-left: ${Spacings.SPACING_12B};

  ${mediaQuery.uptoTablet} {
    padding-left: ${Spacings.SPACING_4B};
  }
`;

const CardWrapper = styled.div`
  width: 65%;

  ${mediaQuery.uptoTablet} {
    width: 100%;
  }
`;

const ATTEMPT_INFO = {
  SUBJECTIVE_AWAITING_EVALUATION:
    'Report card will be accessible after evaluation is completed in 7 days.',
  SUBJECTIVE_UPLOAD_REJECTED: 'You did not upload answers for evaluations',
  OBJECTIVE_IS_LIVE:
    'Report card will be accessible after live test is completed'
};

const AttemptSection = () => {
  const { query } = useRouter();
  const { quizSlug, quizUID } = query;

  const isMobile = useIsMobile();
  const { isSubjective } = useSelectorWithShallowEqual(getCurrentQuizType);
  const previousSessions = useSelectorWithShallowEqual(
    getPreviousSessions(quizUID)
  );

  const {
    isLive,
    isEvaluationPending,
    isSubjectiveAnswerUploadPending,
    isSubjectiveAnswerUploadRejected
  } = useQuizStatus();
  const totalQuestions = useTotalQuestions();

  const handleOnAttemptClick = (uid) => () => {
    pushRoute(
      {
        pathname: '/quiz/[quizSlug]/[quizUID]/attempts/[sessionUID]',
        query: {
          quizSlug,
          quizUID,
          sessionUID: uid
        }
      },
      undefined,
      { shallow: true }
    );
  };

  const renderAttempts = () => {
    const items = [];
    const previousSessionsLength = previousSessions.length;

    for (let i = previousSessionsLength - 1; i >= 0; i -= 1) {
      const isLatest = i === 0;
      const previousSession = previousSessions[i];

      const { uid } = previousSession;
      const title = `Attempt ${previousSessionsLength - i}`;

      if (isSubjective && isLatest && isSubjectiveAnswerUploadPending) {
        // eslint-disable-next-line no-continue
        continue;
      }

      if (isSubjective && isLatest && isEvaluationPending) {
        const subText = `${fullDateWithTime(previousSession.start_time)} • ${
          ATTEMPT_INFO.SUBJECTIVE_AWAITING_EVALUATION
        } ${
          isSubjectiveAnswerUploadRejected
            ? ATTEMPT_INFO.SUBJECTIVE_UPLOAD_REJECTED
            : ''
        }`;

        items.push(<InfoCard key={uid} title={title} subText={subText} />);
      } else if (!isSubjective && isLatest && isLive) {
        const subText = `${fullDateWithTime(previousSession.start_time)} • ${
          ATTEMPT_INFO.OBJECTIVE_IS_LIVE
        }`;

        items.push(<InfoCard key={uid} title={title} subText={subText} />);
      } else {
        items.push(
          <InfoCard
            key={uid}
            title={title}
            subText={fullDateWithTime(previousSession.start_time)}
            onClick={isMobile ? null : handleOnAttemptClick(uid)}
          />
        );
      }
    }

    return items;
  };

  const attemptList = renderAttempts();

  if (!attemptList.length || !totalQuestions) {
    return null;
  }

  return (
    <Wrapper>
      <Heading>Your attempts</Heading>
      <CardWrapper>{attemptList}</CardWrapper>
    </Wrapper>
  );
};

export default AttemptSection;
